import React, { useState } from 'react';
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/cn';
import { useAuth } from '../hooks/useAuth';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Enter your username and password.');
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      const message = err?.response?.data?.error || 'Sign-in failed. Please try again.';
      setError(message);
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  const ready = username.trim().length > 0 && password.length > 0 && !submitting;

  return (
    <div className="min-h-screen w-full flex bg-white">
      <section className="hidden lg:flex flex-col w-[52%] xl:w-[55%] relative px-12 py-10">
        <div className="shrink-0">
          <img src="/aeon-logo.png" alt="Aeon Software" className="h-14 w-auto select-none" draggable="false" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center min-h-0">
          <img
            src="/login-hero.png"
            alt=""
            className="max-h-[64vh] w-auto select-none"
            draggable="false"
          />
        </div>

        <div className="shrink-0 flex justify-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-medium">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            All systems operational
          </span>
        </div>
      </section>

      <section className="flex-1 relative overflow-hidden bg-gradient-to-br from-brand-50 via-sky-50 to-indigo-100">
        <BackgroundDecor />

        <div className="relative z-10 h-full flex items-center justify-center px-6 sm:px-10 py-12">
          <div className="w-full max-w-[420px] animate-slide-up">
            <h1 className="text-[42px] sm:text-[48px] font-semibold tracking-tight text-slate-900 leading-none">
              Log In
            </h1>
            <p className="mt-3 text-[14px] text-slate-600">
              Sign in to access the Route Operations workspace.
            </p>

            <form className="mt-8 flex flex-col gap-5" onSubmit={onSubmit} noValidate>
              <Field
                label="Username"
                icon={Mail}
                value={username}
                onChange={setUsername}
                placeholder="Enter your username"
                autoComplete="username"
                disabled={submitting}
              />

              <Field
                label="Password"
                icon={Lock}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={submitting}
                rightControl={
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
                topRight={
                  <button
                    type="button"
                    className="text-[12.5px] font-medium text-brand-700 hover:text-brand-800 transition-colors"
                    onClick={() => setError('Contact your administrator to reset access.')}
                  >
                    Forgot password?
                  </button>
                }
              />

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700 animate-slide-up"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!ready}
                className={cn(
                  'mt-1 inline-flex items-center justify-center gap-2 h-12 w-full rounded-md text-[14px] font-semibold tracking-wide uppercase text-white shadow-soft transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                  ready
                    ? 'bg-slate-900 hover:bg-slate-800 active:bg-slate-950 hover:-translate-y-0.5'
                    : 'bg-slate-400/80 cursor-not-allowed'
                )}
              >
                {submitting ? 'Signing in…' : 'Login'}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <div className="mt-8 pt-5 border-t border-slate-200/70 flex items-start gap-2 text-[12.5px] text-slate-600">
              <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <p>
                Accounts are provisioned by your administrator. Contact your ops lead for access.
              </p>
            </div>

            <p className="mt-6 text-center text-[11.5px] text-slate-500">
              {new Date().getFullYear()} Aeon Software Pvt. Ltd.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  icon: Icon,
  type = 'text',
  value,
  onChange,
  placeholder,
  rightControl,
  topRight,
  autoComplete,
  disabled
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[12.5px] font-semibold text-slate-700">{label}</label>
        {topRight}
      </div>
      <div
        className={cn(
          'relative flex items-center h-12 rounded-md bg-white border transition-all duration-150',
          focused
            ? 'border-brand-500 shadow-[0_0_0_3px_rgba(99,102,241,0.18)]'
            : 'border-slate-200 shadow-soft hover:border-slate-300'
        )}
      >
        <span className="pl-3 pr-2 text-slate-400">
          <Icon className="h-4 w-4" />
        </span>
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          className="flex-1 h-full bg-transparent text-[14px] text-slate-900 placeholder:text-slate-400 outline-none disabled:cursor-not-allowed"
        />
        {rightControl ? <div className="pr-1.5">{rightControl}</div> : null}
      </div>
    </div>
  );
}

function BackgroundDecor() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-70 pointer-events-none"
      viewBox="0 0 800 900"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <pattern id="login-dots" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.2" fill="rgba(99,102,241,0.16)" />
        </pattern>
      </defs>
      <rect width="800" height="900" fill="url(#login-dots)" />
      <path
        d="M-50 110 Q 300 0 800 220"
        stroke="rgba(99,102,241,0.22)"
        strokeWidth="2"
        strokeDasharray="6 8"
        fill="none"
      />
      <path
        d="M-80 720 Q 320 560 900 760"
        stroke="rgba(14,165,233,0.18)"
        strokeWidth="2"
        strokeDasharray="6 8"
        fill="none"
      />
      <path
        d="M120 -40 Q 0 320 280 540"
        stroke="rgba(99,102,241,0.14)"
        strokeWidth="2"
        strokeDasharray="4 10"
        fill="none"
      />
      <circle cx="660" cy="120" r="3" fill="rgba(99,102,241,0.45)" />
      <circle cx="120" cy="380" r="2.5" fill="rgba(14,165,233,0.4)" />
      <circle cx="540" cy="780" r="3" fill="rgba(99,102,241,0.4)" />
      <circle cx="720" cy="540" r="2.5" fill="rgba(14,165,233,0.4)" />
    </svg>
  );
}
